import React, { useEffect, useMemo, useState } from 'react';
import '../css/sidebar.css';
import { sidebarMenuConfig } from './sidebarConfig';
import { MenuSpec } from './sidebarType';
import { useLocation } from 'react-router-dom';
import { groupPermissionAccessCMSCareer, groupPermissionAccessCMSOverview, groupPermissionAccessContent, groupPermissionAccessInbox, groupPermissionAccessMedia, groupPermissionAccessWorkflow } from '../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';
import { useSafeNavigate } from '../../../../route-controller-mod/route-serv/route-controller/useSafeNavigate';

interface SidebarProps {
  menu?: MenuSpec[];
}

function filterMenuByPermission(menu: MenuSpec[], allow: (path: string) => boolean) {
  return menu
    .filter((m) => allow(m.path))
    .map((m) => ({
      ...m,
      children: m.children?.filter((c) => allow(c.path)),
    }))
    .filter((m) => !m.children || m.children.length > 0);
}

const Sidebar: React.FC<SidebarProps> = ({ menu: menuProp }) => {
  const location = useLocation(); 
  const navigate = useSafeNavigate();
  const activePath = location.pathname;

  const canOverview = groupPermissionAccessCMSOverview.view();
  const canContent = groupPermissionAccessContent.view();
  const canMedia = groupPermissionAccessMedia.view();
  const canCareer = groupPermissionAccessCMSCareer.view();
  const canInbox = groupPermissionAccessInbox.view();
  const canWorkflow = groupPermissionAccessWorkflow.view();

  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const menuBase = useMemo(() => menuProp ?? /* istanbul ignore next */ sidebarMenuConfig(), [menuProp]);

  const allow = (path: string) => {
    if (path === "/overview") return canOverview;
    if (path.startsWith("/content")) return canContent;
    if (path.startsWith("/media")) return canMedia;
    if (path.startsWith("/career")) return canCareer;
    if (path.startsWith("/inbox")) return canInbox;
    /* istanbul ignore next */
    if (path.startsWith("/workflow")) return canWorkflow;
    /* istanbul ignore next */
    return true;
  };

  const menu = useMemo(() => {
    return filterMenuByPermission(menuBase, allow);
  }, [menuBase, canOverview, canContent, canMedia, canCareer, canInbox, canWorkflow]);

  useEffect(() => {
    const activeParent = menu.find(item =>
      item.children?.some(child => activePath.startsWith(child.path))
    );

    if (activeParent) {
      setOpenMenu(activeParent.label);
      return;
    }

    const routeInSidebar = menu.some(item =>
      activePath === item.path ||
      item.children?.some(child => activePath.startsWith(child.path))
    );

    if (!routeInSidebar) {
      setOpenMenu(null);
    }
  }, [activePath, menu]);

  const handleMenuClick = (item: MenuSpec) => {
    const hasChildren = !!item.children?.length;

    if (hasChildren) {
      setOpenMenu(prev => (prev === item.label ? null : item.label));
      return;
    }

    setOpenMenu(null);
    navigate(item.path);
  };

  const handleChildClick = (path: string) => {
    navigate(path);
  };

  const isParentActive = (item: MenuSpec): boolean => {
    const hasChildren = !!item.children?.length;

    if (hasChildren) {
      return activePath === item.path;
    }

    return activePath === item.path || activePath.startsWith(item.path + "/");
  };

  const isParentOpen = (item: MenuSpec): boolean => {
    return (
      item.path === activePath ||
      !!item.children?.some(child => activePath.startsWith(child.path))
    );
  };

  const isChildActive = (path: string): boolean => {
    return activePath === path || activePath.startsWith(path);
  };

  return (
    <div className="scl--cms-layout-sidebar-container">
      <nav className="scl--cms-layout-sidebar-menu">
        {menu.map((item, index) => {
          const isActive = isParentActive(item);
          const isOpen = openMenu === item.label || isParentOpen(item);
          const hasChildren = !!item.children?.length;
          const isFirst = index === 0;

          return (
            <div key={item.id} className="scl--cms-layout-sidebar-group">

              {/* Parent Menu Item */}
              <div className="scl--cms-layout-sidebar-item-wrapper">
                <div
                  className={[
                    'scl--cms-pseudo-before',
                    isActive ? 'submenu-active' : '',
                    isActive && isFirst ? 'first-item-special' : '',
                  ].join(' ').trim()}
                />
                <div
                  className={`scl--cms-pseudo-after ${isActive ? 'submenu-active' : ''}`}
                />

                <button
                  type="button"
                  className={`scl--cms-layout-sidebar-item ${isActive ? 'scl--cms-layout-sidebar-item--active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                  aria-expanded={hasChildren ? isOpen : undefined}
                  aria-label={item.label}
                >
                  <span className="scl--cms-layout-sidebar-item-icon">
                    {item.menuIcon && React.cloneElement(
                      item.menuIcon as React.ReactElement<{ colorCode?: string }>,
                      { colorCode: isActive ? '#323539' : '#8A8B8F' }
                    )}
                  </span>

                  <span className="scl--cms-layout-sidebar-item-label">
                    {item.label}
                  </span>

                  {hasChildren && (
                    <span className={`scl--cms-layout-sidebar-item-chevron ${isOpen ? 'scl--cms-layout-sidebar-item-chevron--open' : ''}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M6 9L12 15L18 9"
                          stroke={isActive ? /* istanbul ignore next */ '#323539' : '#858C95'}
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              </div>

              {/* Submenu */}
              {hasChildren && (
                <div className={`scl--cms-layout-sidebar-submenu ${isOpen ? 'scl--cms-layout-sidebar-submenu--open' : ''}`}>
                  <div className="scl--cms-layout-sidebar-submenu-line" />

                  <div className="scl--cms-layout-sidebar-submenu-items">
                    {item.children!.map((child, childIndex) => {
                      const childActive = isChildActive(child.path);
                      const isFirstChild = childIndex === 0;

                      return (
                        <div
                          key={child.id}
                          className="scl--cms-layout-sidebar-submenu-item-wrapper"
                        >
                          <div
                            className={[
                              'scl--cms-pseudo-before',
                              childActive ? 'submenu-active' : '',
                              childActive && isFirstChild ? 'first-item-special' : '',
                            ].join(' ').trim()}
                          />
                          <div
                            className={`scl--cms-pseudo-after ${childActive ? 'submenu-active' : ''}`}
                          />

                          <button
                            type="button"
                            className={`scl--cms-layout-sidebar-submenu-item ${childActive ? 'scl--cms-layout-sidebar-submenu-item--active' : ''}`}
                            onClick={() => handleChildClick(child.path)}
                            aria-label={child.label}
                            aria-current={childActive ? 'page' : undefined}
                          >
                            <span className="scl--cms-layout-sidebar-submenu-label">
                              {child.label}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;